"use client";

import { createContext, useCallback, useContext, useEffect, ReactNode } from "react";
import { useRouter } from "next/navigation";
import { useMenuData } from "@/context/MenuDataContext";

/**
 * Multi-location is controlled by an explicit per-clinic toggle
 * (`clients.multi_location_enabled`, edited on /config/clinic). When on, the
 * "Locations" side-menu and the per-location UI are revealed; when off the UI
 * stays as simple as a single-location clinic (progressive disclosure).
 *
 * The flag travels on the menu payload (`menuData.client.multi_location_enabled`),
 * so no extra request is needed to gate the UI. Toggling it on /config/clinic
 * calls `refreshMenuData()`, which updates `enabled` here and re-renders the sidebar.
 */
interface MultiLocationContextType {
  /** Whether the multi-location UI (Locations menu + per-location views) is shown. */
  enabled: boolean;
  loading: boolean;
  /** Re-fetch the menu payload so `enabled` reflects the latest saved toggle. */
  refresh: () => Promise<void>;
}

const MultiLocationContext = createContext<MultiLocationContextType | undefined>(
  undefined
);

export function MultiLocationProvider({ children }: { children: ReactNode }) {
  const { menuData, loading, refreshMenuData } = useMenuData();
  const enabled = menuData?.client?.multi_location_enabled ?? false;

  const refresh = useCallback(() => refreshMenuData(), [refreshMenuData]);

  return (
    <MultiLocationContext.Provider value={{ enabled, loading, refresh }}>
      {children}
    </MultiLocationContext.Provider>
  );
}

export function useMultiLocation() {
  const context = useContext(MultiLocationContext);
  if (context === undefined) {
    throw new Error("useMultiLocation must be used within a MultiLocationProvider");
  }
  return context;
}

/**
 * Guard for the /config/locations pages: redirects to the clinic settings when the
 * multi-location feature is off, so the pages aren't reachable by direct URL while the
 * toggle is disabled (mirrors the sidebar gate; keeps the off-state indistinguishable
 * from the pre-feature experience). Waits for the menu payload to load before deciding.
 */
export function useRequireMultiLocationEnabled() {
  const { enabled, loading } = useMultiLocation();
  const router = useRouter();
  useEffect(() => {
    if (!loading && !enabled) router.replace("/config/clinic");
  }, [enabled, loading, router]);
  return { enabled, loading };
}
